import React from 'react';

import { SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT, SCHEMA_TYPE_YOUTUBE } from '../../util/types';
import { richText } from '../../util/richText';

import SectionDetails from './SectionDetails';
import SectionText from './SectionText';
import SectionMultiEnum from './SectionMultiEnum';
import SectionYoutubeVideo from './SectionYoutubeVideo';
import SectionSocialPlatforms from './SectionSocialPlatforms';
import css from './CustomExtendedDataSection.module.css';

/**
 * This component displays extended data that corresponds to asset based custom field
 * configurations. It can be used for displaying listing, user, and transaction custom fields.
 * - Fields with schema types enum, long, and boolean are displayed with a SectionDetails
 *   subcomponent
 * - Fields with schema type multi-enum, text, and youtubeVideoUrl are each displayed
 *   with dedicated subcomponents
 *
 * @param {Object} props props for the component:
 *   - sectionDetailsProps (Object) is passed directly to SectionDetails component
 *   - propsForCustomFields (Array) can be constructed with the pickCustomFieldProps helper
 *   - page is used to set translation key prefixes and component ids
 *   - pickExtendedDataFields is an entity-specific function that determines what data
 *     is selected to be picked from the entity's extended data
 * @return A component that displays the existing extended data according to
 * the provided field configuration
 */
const CustomExtendedDataSection = props => {
  const {
    sectionDetailsProps,
    propsForCustomFields = [],
    idPrefix,
    pickExtendedDataFields,
    className,
    rootClassName,
    isFreelancer,
    isJob,
  } = props;

  const FREELANCER_FIELD_ORDER = ['social_platforms', 'subcategory', 'creation_platform'];
  const JOB_FIELD_ORDER = [
    'category',
    'subcategory',
    'creation_platform',
    'ai_skills',
    'social_platforms',
    'language',
    'expected_deliverables',
    'revisions_included',
    'budget',
    'start_date',
    'end_date',
  ];

  const sortByOrder = order => (a, b) => {
    const aIdx = order.indexOf(a.key);
    const bIdx = order.indexOf(b.key);
    if (aIdx === -1 && bIdx === -1) return 0;
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  };

  const orderedFields = isFreelancer
    ? [...propsForCustomFields].sort(sortByOrder(FREELANCER_FIELD_ORDER))
    : isJob
    ? [...propsForCustomFields].sort(sortByOrder(JOB_FIELD_ORDER))
    : propsForCustomFields;

  return (
    <>
      {!isFreelancer && !isJob && (
        <SectionDetails {...sectionDetailsProps} pickExtendedDataFields={pickExtendedDataFields} />
      )}
      {orderedFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;
        if (schemaType === 'subcategoryDetail') {
          const valueContent = richText(fieldProps.value, { linkify: true });
          return (
            <section key={key} className={css.sectionDetails}>
              <ul className={css.details}>
                <li className={css.detailsRow}>
                  <span className={css.detailLabel}>{fieldProps.label}</span>
                  <span>{valueContent}</span>
                </li>
              </ul>
            </section>
          );
        }
        return schemaType === SCHEMA_TYPE_MULTI_ENUM &&
          key === 'social_platforms' &&
          isFreelancer ? (
          <SectionSocialPlatforms
            key={key}
            className={className}
            rootClassName={rootClassName}
            {...fieldProps}
          />
        ) : schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnum
            key={key}
            idPrefix={idPrefix}
            className={className}
            rootClassName={rootClassName}
            customKey={key}
            {...fieldProps}
          />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionText
            key={key}
            className={className}
            rootClassName={rootClassName}
            {...fieldProps}
          />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideo
            key={key}
            className={className}
            rootClassName={rootClassName}
            {...fieldProps}
          />
        ) : null;
      })}
      {isFreelancer && (
        <SectionDetails
          {...sectionDetailsProps}
          pickExtendedDataFields={pickExtendedDataFields}
          hideTitle
        />
      )}
    </>
  );
};

export default CustomExtendedDataSection;
